import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  DataProviderState,
  DataProviderResponse,
  DataProvider,
} from "./types/data-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: DataProviderState = {
  dataProviders: [],
  allDataProviders: [],
  dataProvider: null,
  selectedDataProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataProvider
export const createDataProvider = createAsyncThunk<
  DataProviderResponse,
  FormData | Omit<DataProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/create", async (dataProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dataProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/data-providers`,
      dataProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data provider" },
    );
  }
});

// READ (fetch all) DataProviders
export const fetchDataProviders = createAsyncThunk<
  DataProviderResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 15,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/data-providers`, {
        params,
        headers,
      });
    }
    return {
      message: res.data.data.message,
      dataProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data providers" },
    );
  }
});

// READ (fetch all providers without pagination)
export const fetchAllDataProviders = createAsyncThunk<
  DataProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/data-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data providers" },
    );
  }
});

// READ (fetch one) DataProvider by ID
export const fetchDataProviderById = createAsyncThunk<
  DataProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/data-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data provider" },
    );
  }
});

// UPDATE DataProvider
export const updateDataProvider = createAsyncThunk<
  DataProvider,
  { id: number; data: FormData | Partial<DataProvider> },
  { rejectValue: ApiError; state: RootState }
>("dataProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/data-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data provider" },
    );
  }
});
// export const updateDataProvider = createAsyncThunk<
//   DataProvider,
//   { id: number; data: FormData | Partial<DataProvider> },
//   { rejectValue: ApiError; state: RootState }
// >("dataProviders/update", async ({ id, data }, thunkAPI) => {
//   const token = thunkAPI.getState().auth.token;
//   if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

//   try {
//     const isFormData = data instanceof FormData;

//     // Use POST with _method=PUT for FormData (Laravel method spoofing)
//     const method = isFormData ? 'post' : 'put';
//     const endpoint = `${API_URL}/data-providers/${id}`;

//     // Add _method field for Laravel when using POST
//     if (isFormData) {
//       data.append('_method', 'PUT');
//     }

//     const res = await axios[method](endpoint, data, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
//       },
//     });

//     return res.data.data;
//   } catch (err: any) {
//     return thunkAPI.rejectWithValue(
//       err.response?.data || { message: "Failed to update data provider" }
//     );
//   }
// });

// DELETE DataProvider
export const deleteDataProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/data-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data provider" },
    );
  }
});

// Slice
const dataProviderSlice = createSlice({
  name: "dataProviders",
  initialState,
  reducers: {
    resetDataProviderState: (state) => {
      state.loading = false;
      state.allLoading = false;
      state.error = null;
      state.allError = null;
      state.success = false;
      state.selectedDataProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createDataProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDataProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dataProvider) {
          state.dataProviders.unshift(action.payload.dataProvider);
        }
      })
      .addCase(createDataProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create data provider";
      })

      // Fetch All
      .addCase(fetchDataProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.dataProviders = action.payload.dataProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDataProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data providers";
      })
      // Fetch All (unpaginated)
      .addCase(fetchAllDataProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllDataProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allDataProviders = action.payload || [];
      })
      .addCase(fetchAllDataProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch data providers";
      })

      // Fetch One
      .addCase(fetchDataProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDataProvider = null;
      })
      .addCase(fetchDataProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDataProvider = action.payload;
      })
      .addCase(fetchDataProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data provider";
      })

      // Update
      .addCase(updateDataProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDataProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dataProviders = state.dataProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedDataProvider?.id === action.payload.id) {
          state.selectedDataProvider = action.payload;
        }
      })
      .addCase(updateDataProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update data provider";
      })

      // Delete
      .addCase(deleteDataProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDataProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dataProviders = state.dataProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteDataProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete data provider";
      });
  },
});

export const { resetDataProviderState } = dataProviderSlice.actions;
export default dataProviderSlice.reducer;
